import type { FixtureScorecard, ScorecardPlayer, ScorecardPlayerDetail } from '@/types';

/** Shaped exactly like the API, so screens behave the same either way. */

const byType = (details: readonly ScorecardPlayerDetail[]): Record<string, number> =>
  Object.fromEntries(details.map((detail) => [String(detail.typeId), detail.value]));

/** `details` and `detailsByTypeId` always agree — the API derives one from the other. */
function player(
  base: Omit<ScorecardPlayer, 'details' | 'detailsByTypeId'>,
  details: ScorecardPlayerDetail[],
): ScorecardPlayer {
  return { ...base, details, detailsByTypeId: byType(details) };
}

const UNITED = 900101;
const CITY = 900102;

const syncedAt = new Date().toISOString();

const gw1Players: ScorecardPlayer[] = [
  player(
    {
      lineupId: 900001,
      playerId: 900001,
      teamId: UNITED,
      positionId: 24,
      lineupTypeId: 11,
      formationField: '1:1',
      jerseyNumber: 1,
      playerName: 'Elliot Grant',
    },
    [
      { typeId: 119, value: 90 },
      { typeId: 84, value: 1 },
      { typeId: 57, value: 3 },
      { typeId: 1535, value: 2 },
      { typeId: 118, value: 5.9 },
    ],
  ),
  player(
    {
      lineupId: 900010,
      playerId: 900010,
      teamId: UNITED,
      positionId: 26,
      lineupTypeId: 11,
      formationField: '3:2',
      jerseyNumber: 8,
      playerName: 'Marcus Sterling',
      captain: true,
    },
    [
      { typeId: 119, value: 90 },
      { typeId: 52, value: 1 },
      { typeId: 79, value: 1 },
      { typeId: 27271, value: 6 },
      { typeId: 118, value: 8.4 },
    ],
  ),
  player(
    {
      lineupId: 900021,
      playerId: 900021,
      teamId: CITY,
      positionId: 27,
      lineupTypeId: 12,
      formationField: '4:2',
      jerseyNumber: 19,
      playerName: 'Rafael Costa',
    },
    [
      { typeId: 119, value: 24 },
      { typeId: 52, value: 1 },
      { typeId: 118, value: 7.1 },
    ],
  ),
];

const gw2Players: ScorecardPlayer[] = [
  player(
    {
      lineupId: 900029,
      playerId: 900029,
      teamId: CITY,
      positionId: 27,
      lineupTypeId: 11,
      formationField: '4:1',
      jerseyNumber: 9,
      playerName: 'Victor Martins',
    },
    [
      { typeId: 119, value: 90 },
      { typeId: 52, value: 1 },
      { typeId: 115, value: 1 },
      { typeId: 118, value: 7.8 },
    ],
  ),
];

export const MOCK_SCORECARDS: Readonly<Record<number, FixtureScorecard>> = {
  900001: {
    fixtureId: 900001,
    seriesId: 900001,
    stateName: 'FT',
    name: 'North London vs Harbour City (GW1)',
    homeTeamId: UNITED,
    awayTeamId: CITY,
    homeScore: 2,
    awayScore: 1,
    length: 90,
    formations: { home: '4-3-3', away: '4-4-2' },
    participants: [
      { teamId: UNITED, location: 'home' },
      { teamId: CITY, location: 'away' },
    ],
    events: [
      {
        eventId: 1,
        typeId: 14,
        minute: 23,
        teamId: UNITED,
        playerId: 900010,
        playerName: 'Marcus Sterling',
        relatedPlayerId: 900009,
        relatedPlayerName: 'Noah Bennett',
        result: '1-0',
      },
      {
        eventId: 2,
        typeId: 19,
        minute: 55,
        teamId: UNITED,
        playerId: 900001,
        playerName: 'Elliot Grant',
        info: 'Yellow card',
      },
      {
        eventId: 3,
        typeId: 18,
        minute: 66,
        teamId: CITY,
        playerId: 900021,
        playerName: 'Rafael Costa',
        relatedPlayerId: 900020,
        relatedPlayerName: 'Leo Hart',
      },
    ],
    timeline: [],
    teamStatistics: [
      { typeId: 45, teamId: UNITED, location: 'home', value: 58 },
      { typeId: 45, teamId: CITY, location: 'away', value: 42 },
      { typeId: 42, teamId: UNITED, location: 'home', value: 14 },
      { typeId: 42, teamId: CITY, location: 'away', value: 9 },
      { typeId: 86, teamId: UNITED, location: 'home', value: 6 },
      { typeId: 86, teamId: CITY, location: 'away', value: 3 },
      { typeId: 34, teamId: UNITED, location: 'home', value: 7 },
      { typeId: 34, teamId: CITY, location: 'away', value: 4 },
    ],
    players: gw1Players,
    status: 'final',
    syncedAt,
    source: 'feed_provider',
    meta: { playersCount: gw1Players.length, detailsCount: 13, eventsCount: 3, teamStatisticsCount: 8 },
  },
  900002: {
    fixtureId: 900002,
    seriesId: 900001,
    stateName: 'FT',
    name: 'Harbour City vs North London (GW1 return)',
    homeTeamId: CITY,
    awayTeamId: UNITED,
    homeScore: 1,
    awayScore: 1,
    length: 90,
    formations: { home: '4-4-2', away: '4-3-3' },
    participants: [
      { teamId: CITY, location: 'home' },
      { teamId: UNITED, location: 'away' },
    ],
    events: [
      {
        eventId: 1,
        typeId: 14,
        minute: 33,
        teamId: CITY,
        playerId: 900029,
        playerName: 'Victor Martins',
        result: '1-0',
      },
    ],
    timeline: [],
    teamStatistics: [
      { typeId: 45, teamId: CITY, location: 'home', value: 51 },
      { typeId: 45, teamId: UNITED, location: 'away', value: 49 },
    ],
    players: gw2Players,
    status: 'final',
    syncedAt,
    source: 'feed_provider',
    meta: { playersCount: gw2Players.length, detailsCount: 4, eventsCount: 1, teamStatisticsCount: 2 },
  },
};
